import { useRemoveCartProduct as useV3RemoveCartProduct } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useNavigateBack } from '@iheartjane/menu';
import { Button, Flex, Typography } from '@iheartjane/reefer';
import { useCallback } from 'react';

import { Modal } from '@jane/kiosk/components';
import type {
  CartConfirmationLoaderType,
  RootLoaderType,
} from '@jane/kiosk/data-access';
import {
  getLocalCartUuid,
  useRemoveCartProduct,
} from '@jane/kiosk/data-access';
import { useKioskSpacing, useTypedRouteLoaderData } from '@jane/kiosk/hooks';
import { useProductTracking } from '@jane/kiosk/providers';

export const CartRemovalConfirmation = () => {
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const { storeId } = useTypedRouteLoaderData<RootLoaderType>('root');
  const cartUuid = getLocalCartUuid();
  const { productId } =
    useTypedRouteLoaderData<CartConfirmationLoaderType>('cartConfirmation');

  const {
    inlineAds: { removeInlineAdId },
    cartToppers: { removeCartTopperId },
  } = useProductTracking();

  const { mutate: removeCartProduct, isPending } = useRemoveCartProduct({});
  const { mutate: v3RemoveCartProduct, isPending: v3IsLoading } =
    useV3RemoveCartProduct({});
  const goBack = useNavigateBack(`stores/${storeId}/menu`);
  const { getKioskSpacing, isIpad } = useKioskSpacing();

  const onRemoveProduct = useCallback(() => {
    const onSuccess = () => {
      removeCartTopperId(productId);
      removeInlineAdId(productId);
      goBack();
    };

    // No cart → nothing to remove; run the cleanup and navigate back rather
    // than firing a mutation against a missing uuid.
    if (!cartUuid) {
      onSuccess();
      return;
    }

    if (useV3Cart) {
      v3RemoveCartProduct(
        {
          appMode: 'kiosk',
          cartUuid,
          productId,
        },
        { onSuccess }
      );
    } else {
      removeCartProduct(
        {
          cart_uuid: cartUuid,
          product_id: productId,
        },
        { onSuccess }
      );
    }
  }, [
    cartUuid,
    goBack,
    productId,
    removeCartProduct,
    removeCartTopperId,
    removeInlineAdId,
    useV3Cart,
    v3RemoveCartProduct,
  ]);

  const removeIsLoading = useV3Cart ? v3IsLoading : isPending;

  return (
    <Modal
      appId="root"
      contentLabel="Remove product confirm"
      onRequestClose={() => goBack()}
      open
      overflow="visible"
      variant="dialogue"
    >
      <Modal.Content>
        <Typography mb={24} variant="h2-semibold">
          Remove this product?
        </Typography>
        <Typography mb={32}>
          Are you sure you want to remove this product from your bag?
        </Typography>
        <Flex flexDirection="row">
          <Button
            ariaLabel="cancel"
            full
            label={
              <Typography
                color="text-main"
                variant={isIpad ? 'body-semibold' : 'h4-semibold'}
              >
                Cancel
              </Typography>
            }
            mr={getKioskSpacing(24, 16)}
            onClick={() => goBack()}
            variant="tertiary"
            size={isIpad ? 'default' : 'large'}
          />
          <Button
            ariaLabel="remove product"
            full
            label={
              <Typography
                color="grays-white"
                variant={isIpad ? 'body-semibold' : 'h4-semibold'}
              >
                Remove
              </Typography>
            }
            disabled={removeIsLoading}
            loading={removeIsLoading}
            onClick={onRemoveProduct}
            variant="destructive"
            size={isIpad ? 'default' : 'large'}
          />
        </Flex>
      </Modal.Content>
    </Modal>
  );
};
